import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useControlTracking } from "../Multilayer/ControlTrackingContext";
import { Dropdown, DropdownItem } from "react-migration/components/Dropdown";
import { Select } from "react-migration/components/Select";
import { useMemo } from "react";
import { ConstraintStatus } from "../Multilayer/layer_types/ConstraintsLayerType/ConstraintsContext/store";

type SourceStatusFilterProps = {
  disabled: boolean;
  hiddenStatuses: ConstraintStatus[];
  setHiddenStatuses: (value: ConstraintStatus[]) => void;
};

export enum ConstraintStatusDropdownValues {
  AdoptedAndEmerging = "AdoptedAndEmerging",
  AdoptedOnly = "AdoptedOnly",
  EmergingOnly = "EmergingOnly",
}

// A little weirdness, but we don't want to touch the existing logic of
// only the HIDDEN items being stored in the state, rather than the values
// to actually show. This makes this change from checkboxes to a dropdown
// less risky
const CONSTRAINT_DROPDOWN_TO_HIDDEN_VALUES_MAP: Record<ConstraintStatusDropdownValues, ConstraintStatus[]> = {
  [ConstraintStatusDropdownValues.AdoptedAndEmerging]: [],
  [ConstraintStatusDropdownValues.AdoptedOnly]: ["draft"],
  [ConstraintStatusDropdownValues.EmergingOnly]: ["adopted"],
};

function getDropdownValueFromHiddenStatuses(
  hiddenStatuses: ConstraintStatus[]
): ConstraintStatusDropdownValues | undefined {
  if (hiddenStatuses.length === 0) {
    return ConstraintStatusDropdownValues.AdoptedAndEmerging;
  } else if (hiddenStatuses.includes("adopted")) {
    return ConstraintStatusDropdownValues.EmergingOnly;
  } else if (hiddenStatuses.includes("draft")) {
    return ConstraintStatusDropdownValues.AdoptedOnly;
  }

  return undefined;
}

export function SourceStatusFilters({ disabled = false, hiddenStatuses, setHiddenStatuses }: SourceStatusFilterProps) {
  const { t } = useTranslation();
  const { trackControlChange } = useControlTracking();

  function handleToggle(value: ConstraintStatusDropdownValues, label: string) {
    trackControlChange({
      filterName: "Status Filter",
      filterState: label,
    });

    setHiddenStatuses(CONSTRAINT_DROPDOWN_TO_HIDDEN_VALUES_MAP[value]);
  }

  const options = useMemo(() => {
    return Object.values(ConstraintStatusDropdownValues).map((status) => ({
      label: t(`constraints.statuses.${status}`),
      value: status,
    }));
  }, [t]);

  return (
    <div className="flex divide-x divide-neutral-300 gap-2">
      <Select
        value={getDropdownValueFromHiddenStatuses(hiddenStatuses)}
        disabled={disabled}
        onValueChange={(value: ConstraintStatusDropdownValues) =>
          handleToggle(value, t(`constraints.statuses.${value}`))
        }
        triggerProps={{
          "data-testid": "dropdown-constraint-source-status-trigger",
        }}
      >
        {options.map((option) => (
          <Select.Option key={option.value} value={option.value}>
            {option.label}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}
